import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, BriefcaseIcon, SlashIcon } from 'lucide-react';
import { bookingUrl, heroStats } from '../data/content';
const stops = ['High school', 'College', 'New grad'];
const SPEED = 0.35; // px per frame

/** The stack of "cards" that scrolls inside the phone screen. */
function ScreenContent({
  reduceMotion
}: {
  reduceMotion: boolean | null;
}) {
  return <div className="space-y-3 p-3" data-id="element-9" data-mp="2pee6zu074k10">
      {/* Age card */}
      <div className="rounded-[1.5rem] bg-pearl-100 p-4 dark:bg-slate-800" data-id="element-10" data-mp="2kcot86ll9s98">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400" data-id="element-11" data-mp="2zb0ruisivb31">
          READY TO STAND OUT?
        </p>

        <p className="mt-4 font-display text-6xl font-semibold leading-none tracking-tight text-slate-900 dark:text-pearl-50" data-id="element-12" data-mp="2u9tiifaqe3u9">
          17<span className="text-butter-500" data-id="element-13" data-mp="zy6crxqawiz3">–</span>24
        </p>
        <p className="mt-2 text-sm leading-snug text-slate-600 dark:text-slate-300" data-id="element-14" data-mp="zzprhjwb6lyr">
          the years that set up the next ten
        </p>

        <div className="mt-5" data-id="element-15" data-mp="cp2xdirpetn5">
          <div className="relative h-1 rounded-full bg-slate-300 dark:bg-slate-600" data-id="element-16" data-mp="cqdx97i13h8z">
            <motion.span aria-hidden="true" initial={{
            left: '0%'
          }} animate={reduceMotion ? {
            left: '50%'
          } : {
            left: ['0%', '50%', '100%', '0%']
          }} transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-butter-500 ring-4 ring-pearl-100 dark:ring-slate-800" data-id="element-17" data-mp="ho5qq5ybnbfa" />
          </div>
          <ul className="mt-2.5 flex justify-between text-[10px] font-medium text-slate-500 dark:text-slate-400" data-id="element-18" data-mp="15erw1pgxyhll">
            {stops.map(stop => <li key={stop} data-id="element-19" data-mp="328mowf9lhu5f">{stop}</li>)}
          </ul>
        </div>
      </div>

      {/* What we work on */}
      <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900" data-id="element-20" data-mp="377zdp5k11ban">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400" data-id="element-21" data-mp="9cbgy5pe218c">
          What we work on
        </p>
        <ul className="mt-3 space-y-2.5" data-id="element-22" data-mp="ncvy6xfnnozq">
          {heroStats.map(stat => <li key={stat.value} className="flex items-start gap-2 text-sm leading-snug text-slate-700 dark:text-slate-200" data-id="element-23" data-mp="ici2mev8q7il">
              <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-butter-400" data-id="element-24" data-mp="x8zc10jvdc2i" />
              {stat.value}
            </li>)}
        </ul>
      </div>

      {/* Briefcase, cancelled */}
      <div className="flex items-center gap-3 rounded-[1.5rem] bg-pearl-100 p-4 dark:bg-slate-800" data-id="element-25" data-mp="1622mczz7x8sx">
        <span className="relative inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white text-slate-400 dark:bg-slate-900 dark:text-slate-500" data-id="element-26" data-mp="121innryzxx4o">
          <BriefcaseIcon className="h-4 w-4" aria-hidden="true" data-id="element-27" data-mp="1b2gk9p73v2d8" />
          <SlashIcon className="absolute h-5 w-5 text-butter-500" aria-hidden="true" data-id="element-28" data-mp="1unh26q5pwydg" />
        </span>
        <span className="text-xs font-medium leading-snug text-slate-600 dark:text-slate-300" data-id="element-29" data-mp="1zmjvjdcc5g5l">
          no stiff briefcase required
        </span>
      </div>

      {/* Packages */}
      <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900" data-id="element-30" data-mp="1krdt10cg7zh0">
        <p className="text-sm font-semibold leading-snug text-slate-900 dark:text-pearl-50" data-id="element-31" data-mp="1bwz9umoss4sm">
          3 customized packages
        </p>
      </div>

      {/* CTA */}
      <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 rounded-[1.5rem] bg-slate-900 p-4 text-sm font-semibold text-pearl-50 transition-colors hover:bg-slate-700 dark:bg-butter-400 dark:text-slate-950 dark:hover:bg-butter-300" data-id="element-32" data-mp="1gp0lq7i1zti4">
        Free 30-min consult
        <ArrowRightIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-33" data-mp="t0hfdxikdqso" />
      </a>
    </div>;
}
export function AgeRangeCard() {
  const reduceMotion = useReducedMotion();
  const screenRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (reduceMotion || paused) return;
    const el = screenRef.current;
    if (!el) return;
    let frame = 0;
    const tick = () => {
      const loopHeight = el.scrollHeight / 2;
      if (loopHeight > 0) {
        el.scrollTop = el.scrollTop >= loopHeight ? el.scrollTop - loopHeight : el.scrollTop + SPEED;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reduceMotion, paused]);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.8,
    delay: 0.55,
    ease: [0.22, 1, 0.36, 1]
  }} className="relative mx-auto w-[15rem] sm:w-[16.5rem]" data-id="element-34" data-mp="3tfbkyczpjz73">
      <div className="relative rounded-[2.25rem] border-4 border-slate-900 bg-white p-1.5 shadow-xl dark:border-slate-700 dark:bg-slate-900" data-id="element-35" data-mp="1mm9gp5jvr22s">
        {/* Notch */}
        <div aria-hidden="true" className="absolute left-1/2 top-2.5 z-20 h-1.5 w-16 -translate-x-1/2 rounded-full bg-slate-200 dark:bg-slate-700" data-id="element-36" data-mp="1v1yz064nwzwt" />

        <div ref={screenRef} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} onTouchStart={() => setPaused(true)} className="no-scrollbar relative h-[24rem] overflow-y-auto rounded-[1.9rem] bg-white pt-4 dark:bg-slate-900" aria-label="Early Edge Coaching at a glance" data-id="element-37" data-mp="23x4wmhts4bo9">
          <ScreenContent reduceMotion={reduceMotion} data-id="element-38" data-mp="1l5lu0c3o2zmr" />
          {/* Duplicate copy keeps the loop seamless */}
          <div aria-hidden="true" data-id="element-39" data-mp="1q4fbsgpzlcsq">
            <ScreenContent reduceMotion={reduceMotion} data-id="element-40" data-mp="1q5i0chzu735h" />
          </div>
        </div>

        {/* Top and bottom fades */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-1.5 top-1.5 z-10 h-8 rounded-t-[1.9rem] bg-gradient-to-b from-white to-transparent dark:from-slate-900" data-id="element-41" data-mp="2wkizewk5s1ga" />
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-1.5 bottom-1.5 z-10 h-10 rounded-b-[1.9rem] bg-gradient-to-t from-white to-transparent dark:from-slate-900" data-id="element-42" data-mp="2bu5a8on0nz4l" />

        {/* Home indicator */}
        <div aria-hidden="true" className="absolute bottom-2.5 left-1/2 z-20 h-1 w-20 -translate-x-1/2 rounded-full bg-slate-300 dark:bg-slate-600" data-id="element-43" data-mp="2lrtdy42logyw" />
      </div>
    </motion.div>;
}